// @flow
import React, {Component, Fragment} from 'react';
import styled from 'styled-components';
import Button from 'antd/lib/button';
import {Link} from '@reach/router';
import {connect} from 'react-redux';
import Menu from 'antd/lib/menu';
import Icon from 'antd/lib/icon';

import type {Battery, ListState, UserAccess, Vehicle} from '../../../lib/types';
import type {AppState} from '../../../ducks/redux';
import {Dropdown, Table} from './../../../components/ui';
import {Panel, Section} from './../../../components/layout';
import Filter, {type BatteryFilterParams} from './Filter';
import Header from '../../../components/layout/Header';
import NavigationTabs from '../components/NavigationTabs';
import {withUserAccess} from './../../withUserAccess';
import {accessTypeEnum} from '../../../lib/enum';
import {
  applyMaskToValue,
  formatDateTimeToString,
  getListInitialState,
  navigate,
  setQueryParams,
} from '../../../lib/helpers';
import {batteryApi, type FetchListParams} from './../../../lib/api';
import CopyBatteryButton from './components/CopyBatteryButton';
import {formatLicensePlateMask} from '../../../components/masked-inputs/LicensePlateInput';
import {DecomissedStatusCode, getDecomissionStatusName} from '../../../lib/api/tire';
import qs from 'query-string';

const StyledPanel = styled(Panel)`
  padding-bottom: 0;
  padding-top: 0;
`;

const PanelTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const canAddBatteryAccess = [
  accessTypeEnum.admin,
  accessTypeEnum.handlingEquipment
];

type Props = {
  location: Location & { state: { page: number } },
  userAccess: UserAccess[],
  employeeBranchOrgUnitId: number
};

type State = ListState<Battery> & {
  filter: BatteryFilterParams
};

/** Вывод списка АКБ */
class List extends Component<Props, State> {
  static defaultProps = {
    location: {}
  };

  getDefaultFilter = () => {
    const canViewAll = this.props.userAccess.some(access =>
      [accessTypeEnum.viewingAllEquipments].includes(access)
    );
    const defaultFilter = canViewAll
      ? {}
      : ({
          nodeId: this.props.employeeBranchOrgUnitId,
          nodeFilterType: 'branchAndChildren'
        }: BatteryFilterParams);
    return defaultFilter;
  };

  state = {
    ...getListInitialState(),
    filter: {},
  };
  
  componentDidMount() {
    const {page, ...filter} = qs.parse(window.location.search);
    this.setState({filter: filter || this.getDefaultFilter()}, () => this.fetchBatteries(page || 1));
  }

  dropdown = {};

  fetchBatteries = async (
    page: number = 1,
    params: FetchListParams<BatteryFilterParams> = {}
  ) => {
    const { filter } = this.state;
    this.setState({ loading: true });
    const { decomissionStatus, ...otherFilters } = filter;
    const fetchParams = {
      ...otherFilters,
      page,
      ...params,
      ...this.getDefaultFilter() // фильтр по оргюнитам в зависимости от клэймов
    };
    if (!!decomissionStatus) {
      fetchParams.decomissionStatus = DecomissedStatusCode[decomissionStatus];
    }
    const { data, totalCount } = await batteryApi.fetchBatteries(fetchParams);
    setQueryParams({ page });
    this.setState({ loading: false, data, totalCount, page });
  };

  renderOverlay = (battery: Battery) => (
    <Menu>
      <Menu.Item>
        <CopyBatteryButton
          battery={battery}
          afterSubmit={() => {
            this.fetchBatteries(this.state.page);
            // $FlowFixMe тут id будет всегда
            if (this.dropdown[battery.id]) {
              this.dropdown[battery.id].onVisibleChange(false);
            }
          }}
        />
      </Menu.Item>
    </Menu>
  );

  columns = [
    { title: '№', dataIndex: 'id', sorter: true, width: 25 },
    {
      title: 'Дата установки ',
      dataIndex: 'installDateByHistory',
      sorter: true,
      render: (date: string) => formatDateTimeToString(date, 'DD.MM.YYYY')
    },
    {
      title: 'Дата снятия ',
      dataIndex: 'removeDateByHistory',
      sorter: true,
      render: (date: string) => formatDateTimeToString(date, 'DD.MM.YYYY')
    },
    { title: 'Мнемокод', dataIndex: 'code', sorter: true },
    { title: 'Наименование', dataIndex: 'name', sorter: true },
    {
      title: 'Марка',
      dataIndex: 'brand',
      sorter: true,
      sorterKey: 'brand'
    },
    {
      title: 'Гос. номер (текущий)',
      dataIndex: 'vehicle',
      stopPropagation: true,
      render: (vehicle?: Vehicle) =>
        vehicle?.licensePlate ? (
          <Link to={`/vehicles/${vehicle.id}`}>
            {applyMaskToValue(vehicle.licensePlate, formatLicensePlateMask)}
          </Link>
        ) : (
          '-'
        )
    },
    {
      title: 'Гос. номер (при снятии)',
      dataIndex: 'wasInstalledOnVehicleLicensePlate',
      stopPropagation: true,
      render: (wasInstalledOnVehicleLicensePlate?: String, record) =>
      wasInstalledOnVehicleLicensePlate ? (
          <Link to={`/vehicles/${record.wasInstalledOnVehicleId}`}>
            {applyMaskToValue(wasInstalledOnVehicleLicensePlate, formatLicensePlateMask)}
          </Link>
        ) : (
          '-'
        ),
        sorter: true,
        sorterKey: 'wasInstalledOnVehicleLicensePlate'
    },
    { title: 'Заводской номер', dataIndex: 'factoryNumber', sorter: true },
    {
      title: 'Статус',
      dataIndex: 'decomissionStatus',
      sorter: true,
      render: (decomissionStatus: string) =>
        getDecomissionStatusName(decomissionStatus)
    },
    {
      title: '',
      width: '30px',
      // Нужно для того, чтобы не отрабатывал onRow
      onCell: () => ({ onClick: (e: any) => e.stopPropagation() }),
      render: (record: Battery) => (
        <Dropdown
          // $FlowFixMe тут id будет всегда
          ref={dropdown => (this.dropdown[record.id] = dropdown)}
          overlay={this.renderOverlay(record)}
        >
          <Icon style={{ fontSize: 16, color: '#2770FF' }} type="ellipsis" />
        </Dropdown>
      )
    }
  ];

  handleRowClick = (id: number) => navigate(`/equipment/batteries/${id}`);

  cleanFilter = () => this.setState({ filter: {} }, this.fetchBatteries);

  applyFilter = (filter: BatteryFilterParams) =>
    this.setState({ filter }, this.fetchBatteries);

  render() {
    const { location, userAccess } = this.props;

    const { data, totalCount, page, pageSize, loading, filter } = this.state;

    const canAdd = userAccess.some(access =>
      canAddBatteryAccess.includes(access)
    );

    return (
      <Fragment>
        <Header
          left={
            <PanelTitle>
              <h1>АКБ</h1>
            </PanelTitle>
          }
          right={canAdd && (
            <Link to="/equipment/batteries/new">
              <Button data-cy="addBattery" type="primary">
                Создать
              </Button>
            </Link>
          )}
        />
        <StyledPanel>
          <NavigationTabs defaultTab="batteries" />
        </StyledPanel>
        <Section>
          <Filter
            filter={filter}
            applyFilter={this.applyFilter}
            cleanFilter={this.cleanFilter}
          />
          <Table
            onRow={record => ({
              onClick: () => this.handleRowClick(record.id)
            })}
            loading={loading}
            data={data.map(battery => ({
              ...battery,
              key: battery.id
            }))}
            fetch={this.fetchBatteries}
            columns={this.columns}
            pagination={{
              page,
              pageSize,
              totalCount,
              location
            }}
          />
        </Section>
      </Fragment>
    );
  }
}

export default connect((state: AppState) => ({
  employeeBranchOrgUnitId: state.auth.profile.employeeBranchOrgUnitId
}))(withUserAccess(List));
